import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Button, Table } from 'react-bootstrap';
import Message from '../../shared_components/Message';
import ToastMessage from '../components/ToastMessage';
import Loader from '../../shared_components/Loader';

import {
  getJitaiVariableList,
  deleteJitaiVariable,
} from '../actions/jitaiActions';
import {
  JITAI_VARIABLE_DELETE_RESET,
  JITAI_VARIABLE_LIST_RESET,
  JITAI_VARIABLE_CREATE_RESET,
  JITAI_VARIABLE_UPDATE_RESET,
} from '../constants/jitaiConstants';

import resetErrorState from '../utils/resetErrorState';

import hasValidLoginCredential from '../../admin_module/utils/hasValidLoginCredential';
import { getAllAccess } from '../../access_module/actions/accessActions';
import { GET_ALL_ACCESS_RESET } from '../../access_module/constants/accessConstants';
import { listStudyDetails } from '../../study_module/actions/studyActions';
import { STUDY_DETAILS_RESET } from '../../study_module/constants/studyConstants';
import UpsertJitaiVariable from '../components/UpsertJitaiVariable';

const JitaiVariablesScreen = ({ history, match }) => {
  const studyId = match.params.studyId;

  // For toast message
  const [toastMessages, setToastMessages] = useState([]);

  const toastOnCloseHandler = async (uid) => {
    setToastMessages((toastMessages) =>
      toastMessages.filter((message) => message.uid !== uid)
    );
  };

  const dispatch = useDispatch();

  //It is needed to check if the logged in user is an admin
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo, logout } = userLogin;

  const jitaiVariableList = useSelector((state) => state.jitaiVariableList);
  const {
    jitaiVariables,
    error: errorGetList,
    loading: loadingGetList,
    success: successGetList,
  } = jitaiVariableList;

  const jitaiVariableDelete = useSelector((state) => state.jitaiVariableDelete);
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = jitaiVariableDelete;

  const jitaiVariableCreate = useSelector((state) => state.jitaiVariableCreate);
  const { success: successCreate } = jitaiVariableCreate;

  const jitaiVariableUpdate = useSelector((state) => state.jitaiVariableUpdate);
  const { success: successUpdate } = jitaiVariableUpdate;

  const allAccess = useSelector((state) => state.allAccess);
  const {
    loading: loadingGetAllAccess,
    error: errorGetAllAccess,
    success: successGetAllAccess,
    allAccessList,
  } = allAccess;

  const studyDetails = useSelector((state) => state.studyDetails);
  const {
    loading: loadingStudy,
    error: errorStudy,
    study: loadedStudy,
    success: successStudy,
  } = studyDetails;

  useEffect(() => {
    if (!hasValidLoginCredential(userInfo, logout, window)) {
      dispatch({ type: JITAI_VARIABLE_LIST_RESET });
      history.push('/login');
    } else {
      if (!loadingGetList && !successGetList && !errorGetList) {
        resetErrorState(dispatch);
        // Put the reset for getting all access just before loading the main content again
        dispatch({ type: GET_ALL_ACCESS_RESET });
        dispatch(getJitaiVariableList(studyId));
      }

      if (!loadingGetAllAccess && !successGetAllAccess && !errorGetAllAccess) {
        dispatch(getAllAccess());
      }

      if (!loadingStudy && !errorStudy && !successStudy) {
        dispatch(listStudyDetails(studyId));
      }

      if (successDelete) {
        dispatch({ type: JITAI_VARIABLE_DELETE_RESET });
        dispatch(getJitaiVariableList(studyId));
      }

      if (successCreate) {
        dispatch(getJitaiVariableList(studyId));
      }

      if (successUpdate) {
        dispatch(getJitaiVariableList(studyId));
      }
    }
  }, [
    dispatch,
    history,
    userInfo,
    studyId,
    logout,
    loadingGetList,
    loadingGetAllAccess,
    loadingStudy,
    errorGetList,
    errorGetAllAccess,
    errorStudy,
    successDelete,
    successCreate,
    successUpdate,
    successGetList,
    successGetAllAccess,
    successStudy,
  ]);

  const deleteJitaiVariableHandler = (jitaiVariable) => {
    if (jitaiVariable.implemented) {
      window.alert('Deletion is not allowed for implemented variable. Contact administrator for further action.');
      return;
    }
    if (
      window.confirm('Are you sure you want to delete this jitai variable?')
    ) {
      resetErrorState(dispatch);
      dispatch(deleteJitaiVariable(studyId, jitaiVariable._id));
    }
  };

  const headingStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
  };

  return (
    <>
      {/* Display toast messages */}
      <div
        style={{
          position: 'absolute',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          height: '80vh',
          width: '80vw',
          zIndex: 1,
        }}
      >
        {toastMessages.map((toastMessage) => (
          <ToastMessage
            key={toastMessage.uid}
            variant={toastMessage.variant}
            onClose={() => toastOnCloseHandler(toastMessage.uid)}
          >
            {toastMessage.message}
          </ToastMessage>
        ))}
      </div>

      <Row className='align-items-center'>
        <Col>
          <h1 style={headingStyle}>Jitai Variables</h1>
          {loadedStudy && (
            <p className='mb-3'>
              <strong>Study:</strong> {loadedStudy.chin_name} (
              {loadedStudy.study_code})
            </p>
          )}
        </Col>
        <UpsertJitaiVariable
          studyId={studyId}
          jitaiVariableList={jitaiVariables}
          updatedJitaiVariable={undefined}
        ></UpsertJitaiVariable>
      </Row>

      {errorDelete && <Message variant='danger'>{errorDelete}</Message>}
      {loadingGetList || loadingDelete ? (
        <Loader />
      ) : errorGetList ? (
        <Message variant='danger'>{errorGetList}</Message>
      ) : (
        <>
          <p>
            Manage jitai variables for this study. These variables are computed
            to form the basis of decision rule for showing the appropiate
            treatment content; and can also be embedded in a Jitai message.
          </p>
          <p>
            Please note that you only need to provide description for the
            definition of each variable. The actual implementation will be done
            by developer later on.
          </p>
          <Table striped bordered hover responsive className='table-sm'>
            <thead>
              <tr>
                <th>Code</th>
                <th>Implemented</th>
                <th>Created At</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {!logout &&
                jitaiVariables.map((jitaiVariable) => (
                  <tr key={jitaiVariable._id}>
                    <td className='align-middle'>{jitaiVariable.code}</td>
                    <td className='align-middle'>
                      {jitaiVariable.implemented ? (
                        <span className='text-success'>
                          <i className='fas fa-check'></i> Yes
                        </span>
                      ) : (
                        <span className='text-warning'>
                          <i className='fas fa-times'></i> No
                        </span>
                      )}
                    </td>
                    <td className='align-middle'>
                      {new Date(jitaiVariable.createdAt).toLocaleDateString()}
                    </td>
                    <td className='align-middle'>
                      <UpsertJitaiVariable
                        studyId={studyId}
                        jitaiVariableList={jitaiVariables}
                        updatedJitaiVariable={jitaiVariable}
                      ></UpsertJitaiVariable>

                      <Button
                        variant='danger'
                        className='btn-sm mx-1'
                        onClick={() =>
                          deleteJitaiVariableHandler(jitaiVariable)
                        }
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  );
};

export default JitaiVariablesScreen;
